// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import type { PreFetchArgs } from '@gandi/app-runner/server';
import {
  selectHelpdeskUrl,
  selectIsAuthenticatedUser,
} from '@gandi/authentication';
import { selectAllDiscounts, selectCartAffiliate } from '@gandi/cart';

import { useTranslator } from '@gandi/translate';
import { Base, useDialogState } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { stopSSE } from 'src/modules/app/sse';

import DomainPageTitle from '../../components/DomainPageTitle';
import ReferAFriendWelcomeBox from '../../components/ReferAFriendWelcomeBox';
import { useQuery } from '../../helpers/withRouter';
import type { FullState, PreFetchReturn } from '../../types';
import {
  change as setFilter,
  reset as resetFilter,
} from '../modules/actions/filters';
import { set as setSearch } from '../modules/actions/search';
import { set as setSource } from '../modules/actions/source';

import { submitSearch } from '../modules/actions/suggestions';

import { selectIsUserSameAsReferer } from '../modules/selectors/discount';
import { selectNonProvided } from '../modules/selectors/results';
import {
  selectAlteredSearch,
  selectFilterFailed,
  selectSearchError,
  selectSearchValue,
} from '../modules/selectors/search';

import AlteredSearchWarning from './AlteredSearchWarning';
import { CommercialArguments } from './CommercialArguments';
import DomainInput from './DomainInput';
import { DomainList } from './DomainList';
import FilterFailedRow from './FilterFailedRow';
import Filters from './Filters';
import PhasesDialog from './PhasesDialog';
import PhoneFloatingBox from './PhoneFloatingBox';
import { ProvidedArea } from './ProvidedArea';
import { HighLightedSuggestion } from './ProvidedArea/HighlightedSuggestion';
import Refer from './Refer';
import WelcomeOffer from './WelcomeOffer';

import styles from './DomainSuggest.scss';

const inputId = 'search';

type MaybeCleanUpFn = () => void;

export type Query = {
  source?: string,
  search?: string,
  tld?: string,
  bulk?: '1',
  options?: '1',
};

function DomainSuggest(): React.MixedElement {
  const translator = useTranslator();
  const dispatch = useDispatch();
  const query = useQuery<Query>();

  const affiliate = useAppSelector(selectCartAffiliate);
  const isFilterFailed = useAppSelector(selectFilterFailed);
  const results = useAppSelector(selectNonProvided);
  const hasResults = results.length > 0;
  const search = useAppSelector(selectSearchValue);

  const error = useAppSelector(selectSearchError);
  const supportURL = useAppSelector(selectHelpdeskUrl);
  const isUserSameAsReferer = useAppSelector(selectIsUserSameAsReferer);
  const allDiscounts = useAppSelector(selectAllDiscounts);
  const hasDiscounts = allDiscounts.length > 0;
  const isAuthenticatedUser = useAppSelector(selectIsAuthenticatedUser);
  const alteredSearch = useAppSelector(selectAlteredSearch);

  /**
   * Autostart a search if a search is found in the querystring.
   * Always reset the search source.
   * (useful when coming from a preFetch w/ ?source=parking)
   */
  React.useEffect(() => {
    // FIXME: stopSSE is already a cleanup function, so it calls
    // a function that only returns a function on unmount.
    // It doesn't stop the SSE poller as far as I understand
    // $FlowFixMe[incompatible-type]
    const componentWillUnmount: MaybeCleanUpFn = () => stopSSE;
    // we save the this state on mount to keep the info box whatever cart change
    // to avoid the content to move up when adding the free .fr domain
    if (query.search) {
      if (query.search === search && hasResults) {
        return componentWillUnmount;
      }
      const newSearch = hasResults ? query.search : search || query.search;
      dispatch(submitSearch(newSearch));
    }
    // $FlowFixMe[not-an-array]
    dispatch(setSource());
    return componentWillUnmount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The objects are not the same reference, so Set wont work here
  const alteredChanges = alteredSearch
    ? [
        ...new Map(
          (alteredSearch.changes || []).map(item => [item.code, item]),
        ).values(),
      ]
    : [];

  const title = <DomainPageTitle process="create" />;

  const [isBulkLoading, setBulkLoading] = React.useState(false);
  const onBulkCancel = () => {
    setBulkLoading(false);
  };
  const phasesDialogState = useDialogState();

  return (
    <>
      <DomainInput
        id={inputId}
        title={title}
        process="create"
        label={translator.__('Register a domain name')}
      >
        <Filters translator={translator} />
      </DomainInput>
      {isFilterFailed && (
        <FilterFailedRow
          translator={translator}
          onClickResetFilter={(e: SyntheticMouseEvent<>) => {
            e.preventDefault();
            // $FlowFixMe[not-an-array]
            dispatch(resetFilter());
          }}
        />
      )}
      {affiliate &&
        ((!isUserSameAsReferer && hasDiscounts) || !isAuthenticatedUser) && (
          <>
            <ReferAFriendWelcomeBox affiliate={affiliate} />
            <WelcomeOffer translator={translator} />
          </>
        )}
      {isAuthenticatedUser &&
        affiliate &&
        (isUserSameAsReferer || !hasDiscounts) && <Refer />}

      {alteredChanges.length > 0 &&
        alteredChanges.map(change => (
          <AlteredSearchWarning change={change} key={change.code} />
        ))}

      {error && (
        <Base backgroundColor="surface" padding="small" marginBottom="base">
          <Base
            tagName="p"
            margin="none"
            dangerouslySetInnerHTML={translator.__(
              'Something seems broken right now. Please retry or <a href="%(supportURL)s">contact the Customer Care.</a>',
              { withHTML: true, supportURL },
            )}
          />
        </Base>
      )}
      {search ? (
        <div>
          <ProvidedArea
            phasesDialogState={phasesDialogState}
            isBulkLoading={isBulkLoading}
            setBulkLoading={setBulkLoading}
          />
          {/* TODO: move Highlighted into ProvidedArea */}
          <HighLightedSuggestion phasesDialogState={phasesDialogState} />
          <DomainList
            className={styles.domains}
            phasesDialogState={phasesDialogState}
          />
        </div>
      ) : (
        <CommercialArguments />
      )}
      <PhoneFloatingBox />
      <PhasesDialog dialog={phasesDialogState} onBulkCancel={onBulkCancel} />
    </>
  );
}

/**
 * - Save the value of the 'search' query params if present in
 * the query string (to automatically start a search from an url)
 */
DomainSuggest.preFetch = ({
  query,
  dispatch,
}: PreFetchArgs<{}, void, Query, FullState>):
  | Promise<void>
  | PreFetchReturn<> => {
  const { source, search, tld } = query;

  if (search || tld) {
    const promises = [];

    if (tld) {
      // $FlowFixMe[not-an-array]
      promises.push(dispatch(setFilter({ name: 'tlds', value: tld })));
    }

    if (search) {
      // $FlowFixMe[not-an-array]
      promises.push(dispatch(setSearch(search)));
    }

    if (source) {
      // $FlowFixMe[not-an-array]
      promises.push(dispatch(setSource(source)));
    }

    return Promise.allSettled(promises);
  }

  return Promise.resolve();
};

export default DomainSuggest;
